/**
 * Direct SDK API Service - Simplified approach with global error handling
 *
 * This removes the unnecessary wrapper layer and provides direct access
 * to the generated SDK with minimal configuration and centralized error handling.
 *
 * Build: 2026-02-01-v2
 */

import { OpenAPI } from "@/client/core/OpenAPI"
import {
  AdminService,
  AttendancesService,
  AuthenticationService,
  ClassSessionsService,
  ClassTemplatesService,
  EnrollmentsService,
  FilesService,
  HealthService,
  HouseholdsService,
  InvoicesService,
  SessionsService,
  StudentsService,
  UsersService,
} from "@/client/sdk.gen"
import { useAuthStore } from "@/stores/auth"
import { createAppError, isAuthError, logError } from "@/utils/errorHandler"
import axios, {
  AxiosHeaders,
  type AxiosRequestConfig,
  type AxiosRequestHeaders,
} from "axios"

declare module "axios" {
  interface AxiosRequestConfig {
    metadata?: {
      requestId?: string
    }
  }
}

const REQUEST_ID_HEADER = "X-Request-ID"
const CSRF_TOKEN_HEADER = "X-CSRF-Token"
const CSRF_COOKIE_NAME = "csrf_token"

// API client version - used to verify correct build is deployed
export const API_CLIENT_VERSION = "2026.02.01.v3"

type AccessTokenResolver = () => string | null

let resolveAccessToken: AccessTokenResolver = () => {
  const { accessToken } = useAuthStore.getState()
  return accessToken
}

export function setAuthTokenResolver(resolver: AccessTokenResolver) {
  resolveAccessToken = resolver
}

let interceptorsRegistered = false
let csrfToken: string | null = null

type AuthRedirectHandler = (path?: string) => void
let authRedirectHandler: AuthRedirectHandler | null = null

type CacheClearHandler = () => void
let cacheClearHandler: CacheClearHandler | null = null

export function setAuthRedirectHandler(handler: AuthRedirectHandler | null) {
  authRedirectHandler = handler
}

export function setCacheClearHandler(handler: CacheClearHandler | null) {
  cacheClearHandler = handler
}

/**
 * Get CSRF token from cookie
 */
const getCsrfTokenFromCookie = (): string | null => {
  if (typeof document === "undefined") {
    return null
  }

  const name = `${CSRF_COOKIE_NAME}=`
  const decodedCookie = decodeURIComponent(document.cookie)
  const cookies = decodedCookie.split(";")

  for (let cookie of cookies) {
    cookie = cookie.trim()
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length)
    }
  }

  return null
}

/**
 * Store CSRF token from response header
 */
const storeCsrfTokenFromResponse = (response: any): void => {
  const token =
    response.headers?.[CSRF_TOKEN_HEADER.toLowerCase()] ||
    response.headers?.[CSRF_TOKEN_HEADER]

  if (token) {
    csrfToken = token
  }
}

/**
 * Get current CSRF token (from memory or cookie)
 */
export const getCsrfToken = (): string | null => {
  return csrfToken || getCsrfTokenFromCookie()
}

/**
 * Clear CSRF token
 */
export const clearCsrfToken = (): void => {
  csrfToken = null
}

const generateRequestId = (): string => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID()
  }

  return `req_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
}

const readHeaderValue = (
  headers: Record<string, unknown> | undefined,
  key: string,
): string | undefined => {
  if (!headers) {
    return undefined
  }

  const direct = headers[key]
  if (typeof direct === "string") {
    return direct
  }

  const lower = headers[key.toLowerCase()]
  if (typeof lower === "string") {
    return lower
  }

  return undefined
}

// Create axios instance with consistent configuration
const axiosInstance = axios.create({
  withCredentials: true,
})

/**
 * Initialize the OpenAPI client configuration with global error handling
 */
export function initializeApiClient() {
  const configuredBaseUrl =
    import.meta.env.VITE_API_URL ?? import.meta.env.VITE_API_BASE_URL ?? ""

  // Set base URL
  OpenAPI.BASE = configuredBaseUrl
  axiosInstance.defaults.baseURL = OpenAPI.BASE
  ;(OpenAPI as any).HTTP = axiosInstance

  // Set credentials
  OpenAPI.WITH_CREDENTIALS = true
  OpenAPI.CREDENTIALS = "include"

  // Set up authentication token resolver
  OpenAPI.TOKEN = async () => resolveAccessToken() ?? ""

  if (!interceptorsRegistered) {
    // Configure axios instance with auth interceptor
    axiosInstance.interceptors.request.use((config) => {
      const accessToken = resolveAccessToken()
      const requestId = generateRequestId()

      const headers: AxiosRequestHeaders = AxiosHeaders.from(
        config.headers ?? {},
      )

      headers.set(REQUEST_ID_HEADER, requestId)
      headers.set("X-Client-Request-ID", requestId)

      config.metadata = {
        ...(config.metadata ?? {}),
        requestId,
      }

      if (accessToken) {
        headers.set("Authorization", `Bearer ${accessToken}`)
      }

      // Add CSRF token to state-changing requests (POST, PUT, PATCH, DELETE)
      const method = config.method?.toUpperCase()
      if (method && ["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
        const token = getCsrfToken()
        if (token) {
          headers.set(CSRF_TOKEN_HEADER, token)
        }
      }

      config.headers = headers
      return config
    })

    // Global response interceptor for error handling (both OpenAPI and axios)
    const handleApiError = (error: any) => {
      const appError = createAppError(error)

      const config: AxiosRequestConfig | undefined = error.config
      const headerRequestId = readHeaderValue(
        config?.headers as Record<string, unknown> | undefined,
        REQUEST_ID_HEADER,
      )
      const clientRequestId =
        (typeof config?.metadata?.requestId === "string" &&
          config.metadata.requestId) ||
        (typeof headerRequestId === "string" && headerRequestId) ||
        undefined

      const serverRequestId = readHeaderValue(
        error.response?.headers as Record<string, unknown> | undefined,
        REQUEST_ID_HEADER,
      )
      const correlationId = serverRequestId ?? clientRequestId

      const method = error.config?.method?.toUpperCase() ?? "UNKNOWN"
      const url = error.config?.url ?? "unknown"

      // Log error for debugging with correlation identifiers
      logError(appError, `API Request: ${method} ${url}`, {
        requestId: correlationId,
        clientRequestId,
        serverRequestId,
      })

      // Handle authentication errors globally
      if (isAuthError(appError)) {
        useAuthStore.getState().clearAuth()
        clearCsrfToken() // Clear CSRF token on auth failure
        if (cacheClearHandler) {
          try {
            cacheClearHandler()
          } catch (cacheError) {
            if (import.meta.env.DEV) {
              console.warn(
                "Failed to clear query cache after auth error",
                cacheError,
              )
            }
          }
        }

        const redirectToLogin = () => {
          const target = "/login"
          if (authRedirectHandler) {
            authRedirectHandler(target)
            return
          }

          if (typeof window !== "undefined") {
            if (!window.location.pathname.includes("/login")) {
              window.location.assign(target)
            }
          }
        }

        redirectToLogin()
      }

      // Re-throw as AppError for consistent handling
      throw appError
    }

    // Apply to axios instance
    axiosInstance.interceptors.response.use(
      (response) => {
        // Store CSRF token from response if present
        storeCsrfTokenFromResponse(response)
        return response
      },
      (error) => {
        // Try to store CSRF token even on error (e.g., validation errors still return it)
        if (error.response) {
          storeCsrfTokenFromResponse(error.response)
        }
        handleApiError(error)
      },
    )

    interceptorsRegistered = true
  }
}

/**
 * Direct access to generated SDK services
 * No wrapper layer - use the generated types and methods directly
 */
export const api = {
  admin: AdminService,
  attendances: AttendancesService,
  auth: AuthenticationService,
  classSessions: ClassSessionsService,
  classTemplates: ClassTemplatesService,
  enrollments: EnrollmentsService,
  files: FilesService,
  health: HealthService,
  households: HouseholdsService,
  instance: axiosInstance,
  invoices: InvoicesService,
  sessions: SessionsService,
  students: StudentsService,
  users: UsersService,
  // Expose axios instance for endpoints not in generated SDK
}

// Initialize on module load
initializeApiClient()

export default api
